// frontend/src/features/campaign/components/create/CertificateVerificationModal.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  computeCampaignGovernanceHash,
  detectCampaignModifications,
} from '../../utils/campaignUtils';

export default function CertificateVerificationModal({
  isOpen,
  onClose,
  formData = null,
  initialCampaign = null,
  approvedGates = null,
  certId = 'GOV-CERT-2026-633518',
  certHash = 'sha256:021efde7ab1526f5104185682d0bf1438058f0723ada5d936bec0268da0db21e',
  signedBy = 'Marcus Yeo',
  approverRole = 'Workspace Owner',
  certifiedAt = '17 Sept 2026, 15:02',
  campaignName = 'Campaign Strategy',
  workspaceName = 'Primary Workspace',
  jurisdiction = 'Malaysia (PDPA 2010)',
}) {
  const [isVerifying, setIsVerifying] = useState(false);
  const [isCopiedCertified, setIsCopiedCertified] = useState(false);
  const [isCopiedLive, setIsCopiedLive] = useState(false);

  // Compute live checksum from current formData
  const liveHash = useMemo(() => {
    if (!formData && !initialCampaign) return certHash;
    return computeCampaignGovernanceHash(formData || initialCampaign, approvedGates);
  }, [formData, initialCampaign, approvedGates, certHash]);

  // Compute baseline hash
  const baselineHash = useMemo(() => {
    return certHash || computeCampaignGovernanceHash(initialCampaign);
  }, [certHash, initialCampaign]);

  // Detect detailed modifications across all 6 tabs
  const detectedDiffs = useMemo(() => {
    if (!initialCampaign || !formData) return [];
    return detectCampaignModifications(initialCampaign, formData, approvedGates);
  }, [initialCampaign, formData, approvedGates]);

  // Check if live strategy matches certified baseline
  const isMatch = detectedDiffs.length === 0;

  useEffect(() => {
    if (isOpen) {
      setIsVerifying(true);
      const timer = setTimeout(() => {
        setIsVerifying(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCopyCertifiedHash = () => {
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(baselineHash);
      }
      setIsCopiedCertified(true);
      setTimeout(() => setIsCopiedCertified(false), 2500);
    } catch (_err) {
      // ignore
    }
  };

  const handleCopyLiveHash = () => {
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(liveHash);
      }
      setIsCopiedLive(true);
      setTimeout(() => setIsCopiedLive(false), 2500);
    } catch (_err) {
      // ignore
    }
  };

  const handleReverify = () => {
    setIsVerifying(true);
    setTimeout(() => {
      setIsVerifying(false);
    }, 400);
  };

  return (
    <div
      className="cmp-modal-backdrop"
      onClick={onClose}
      style={{
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        boxSizing: 'border-box',
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <div
        className="cmp-modal-container"
        style={{
          width: '100%',
          maxWidth: '660px',
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 20px 40px -15px rgba(0, 0, 0, 0.3)',
          background: '#ffffff',
          border: '1px solid #e2e8f0',
          boxSizing: 'border-box',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div
          style={{
            background: '#ffffff',
            color: '#0f172a',
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            borderBottom: '1px solid #e2e8f0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '8px',
                background: isMatch ? '#f0fdf4' : '#fffbeb',
                border: `1px solid ${isMatch ? '#bbf7d0' : '#fde68a'}`,
                color: isMatch ? '#16a34a' : '#d97706',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
                flexShrink: 0,
              }}
            >
              <i className={isMatch ? 'fas fa-shield-alt' : 'fas fa-shield-halved'} />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h3
                  style={{
                    color: '#0f172a',
                    fontSize: '15.5px',
                    fontWeight: 600,
                    margin: 0,
                    letterSpacing: '-0.2px',
                  }}
                >
                  Cryptographic Certificate Verification
                </h3>
                <span
                  style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    padding: '1px 6px',
                    borderRadius: '4px',
                    background: '#f1f5f9',
                    color: '#475569',
                    border: '1px solid #e2e8f0',
                    letterSpacing: '0.3px',
                  }}
                >
                  SHA-256
                </span>
              </div>
              <p style={{ color: '#64748b', fontSize: '12px', margin: '2px 0 0 0' }}>
                Immutable Governance &amp; Digital Attestation Audit
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Close modal"
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              color: '#64748b',
              width: '30px',
              height: '30px',
              fontSize: '13px',
              cursor: 'pointer',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#0f172a';
              e.currentTarget.style.background = '#f1f5f9';
              e.currentTarget.style.borderColor = '#cbd5e1';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#64748b';
              e.currentTarget.style.background = '#f8fafc';
              e.currentTarget.style.borderColor = '#e2e8f0';
            }}
          >
            <i className="fas fa-times" />
          </button>
        </div>

        {/* Modal Body with scrolling */}
        <div
          style={{
            padding: '18px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px',
            overflowY: 'auto',
            flex: 1,
            minHeight: 0,
            background: '#ffffff',
          }}
        >
          {/* Verification Status Card */}
          {isVerifying ? (
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '16px',
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <i
                className="fas fa-circle-notch fa-spin"
                style={{ fontSize: '20px', color: '#3b82f6' }}
              />
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#1e293b' }}>
                Computing Deterministic SHA-256 Hash...
              </div>
              <div style={{ fontSize: '12px', color: '#64748b' }}>
                Verifying strategy payload against the certified governance baseline ledger proof.
              </div>
            </div>
          ) : isMatch ? (
            <div
              style={{
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: '#16a34a',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  flexShrink: 0,
                }}
              >
                <i className="fas fa-check" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
                >
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#166534' }}>
                    100% Verified &amp; Cryptographically Valid
                  </span>
                  <span
                    style={{
                      background: '#dcfce7',
                      color: '#15803d',
                      border: '1px solid #86efac',
                      fontSize: '10.5px',
                      padding: '1px 7px',
                      borderRadius: '4px',
                      fontWeight: 700,
                    }}
                  >
                    MATCH
                  </span>
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#15803d',
                    marginTop: '2px',
                    lineHeight: '1.4',
                  }}
                >
                  Zero unauthorized modifications detected. Current strategy perfectly matches the
                  certified governance baseline.
                </div>
              </div>
            </div>
          ) : (
            <div
              style={{
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: '8px',
                padding: '12px 14px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  background: '#d97706',
                  color: '#ffffff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  flexShrink: 0,
                  marginTop: '1px',
                }}
              >
                <i className="fas fa-exclamation-triangle" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}
                >
                  <span style={{ fontSize: '13px', fontWeight: 600, color: '#92400e' }}>
                    Strategy Modified Since Certification
                  </span>
                  <span
                    style={{
                      background: '#fef3c7',
                      color: '#b45309',
                      border: '1px solid #fcd34d',
                      fontSize: '10.5px',
                      padding: '1px 7px',
                      borderRadius: '4px',
                      fontWeight: 700,
                    }}
                  >
                    UNMATCH / MISMATCH
                  </span>
                </div>
                <div
                  style={{
                    fontSize: '12px',
                    color: '#78350f',
                    marginTop: '2px',
                    lineHeight: '1.4',
                  }}
                >
                  Campaign configuration was modified after certification. The live parameters
                  differ from the signed ledger proof.
                </div>
              </div>
            </div>
          )}

          {/* Hash Value Comparison Box */}
          <div
            style={{
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '14px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}
          >
            {/* Certified Hash */}
            <div>
              <div
                style={{
                  fontSize: '11px',
                  fontWeight: 600,
                  color: '#475569',
                  marginBottom: '5px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <i className="fas fa-fingerprint" style={{ color: '#64748b' }} /> Certified
                  Baseline Hash
                </span>
                <span
                  style={{
                    color: '#15803d',
                    fontSize: '10px',
                    fontWeight: 600,
                    background: '#dcfce7',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    border: '1px solid #bbf7d0',
                  }}
                >
                  Ledger Proof
                </span>
              </div>
              <div
                style={{
                  background: '#ffffff',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '6px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '8px',
                }}
              >
                <span
                  style={{
                    minWidth: 0,
                    flex: 1,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: '11px',
                    color: '#0f172a',
                    wordBreak: 'break-all',
                    letterSpacing: '-0.1px',
                  }}
                >
                  {baselineHash}
                </span>
                <button
                  type="button"
                  onClick={handleCopyCertifiedHash}
                  style={{
                    background: '#f1f5f9',
                    border: '1px solid #cbd5e1',
                    color: '#334155',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '11px',
                    fontWeight: 500,
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.15s ease',
                  }}
                  title="Copy Certified Hash"
                >
                  {isCopiedCertified ? (
                    <>
                      <i className="fas fa-check" style={{ color: '#16a34a' }} /> Copied
                    </>
                  ) : (
                    <>
                      <i className="fas fa-copy" /> Copy
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Current Modified Hash (Only shown if unmatch) */}
            {!isMatch && (
              <div>
                <div
                  style={{
                    fontSize: '11px',
                    fontWeight: 600,
                    color: '#991b1b',
                    marginBottom: '5px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <i className="fas fa-code-commit" style={{ color: '#dc2626' }} /> Current Live
                    Checksum
                  </span>
                  <span
                    style={{
                      color: '#b91c1c',
                      fontSize: '10px',
                      fontWeight: 600,
                      background: '#fee2e2',
                      padding: '1px 6px',
                      borderRadius: '4px',
                      border: '1px solid #fca5a5',
                    }}
                  >
                    Uncommitted Draft
                  </span>
                </div>
                <div
                  style={{
                    background: '#fff1f2',
                    border: '1px solid #fecdd3',
                    borderRadius: '6px',
                    padding: '6px 10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '8px',
                  }}
                >
                  <span
                    style={{
                      minWidth: 0,
                      flex: 1,
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      fontSize: '11px',
                      color: '#9f1239',
                      wordBreak: 'break-all',
                      letterSpacing: '-0.1px',
                    }}
                  >
                    {liveHash}
                  </span>
                  <button
                    type="button"
                    onClick={handleCopyLiveHash}
                    style={{
                      background: '#ffe4e6',
                      border: '1px solid #fecdd3',
                      color: '#9f1239',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '11px',
                      fontWeight: 500,
                      flexShrink: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      transition: 'all 0.15s ease',
                    }}
                    title="Copy Live Hash"
                  >
                    {isCopiedLive ? (
                      <>
                        <i className="fas fa-check" style={{ color: '#e11d48' }} /> Copied
                      </>
                    ) : (
                      <>
                        <i className="fas fa-copy" /> Copy
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Comparison Details Grid */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '8px 16px',
                paddingTop: '10px',
                borderTop: '1px solid #e2e8f0',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <span style={{ color: '#64748b', fontSize: '11px', display: 'block' }}>
                  Certificate ID
                </span>
                <span
                  style={{
                    color: '#0f172a',
                    fontSize: '12px',
                    fontWeight: 600,
                    wordBreak: 'break-word',
                  }}
                >
                  {certId}
                </span>
              </div>
              <div style={{ minWidth: 0 }}>
                <span style={{ color: '#64748b', fontSize: '11px', display: 'block' }}>
                  Certified By
                </span>
                <span
                  style={{
                    color: '#0f172a',
                    fontSize: '12px',
                    fontWeight: 600,
                    wordBreak: 'break-word',
                  }}
                >
                  {signedBy}{' '}
                  <span style={{ color: '#64748b', fontWeight: 400 }}>({approverRole})</span>
                </span>
              </div>
              <div style={{ minWidth: 0 }}>
                <span style={{ color: '#64748b', fontSize: '11px', display: 'block' }}>
                  Attestation Date
                </span>
                <span
                  style={{
                    color: '#0f172a',
                    fontSize: '12px',
                    fontWeight: 600,
                    wordBreak: 'break-word',
                  }}
                >
                  {certifiedAt}
                </span>
              </div>
              <div style={{ minWidth: 0 }}>
                <span style={{ color: '#64748b', fontSize: '11px', display: 'block' }}>
                  Jurisdiction
                </span>
                <span
                  style={{
                    color: '#0f172a',
                    fontSize: '12px',
                    fontWeight: 600,
                    wordBreak: 'break-word',
                  }}
                >
                  {jurisdiction}
                </span>
              </div>
              <div style={{ minWidth: 0 }}>
                <span style={{ color: '#64748b', fontSize: '11px', display: 'block' }}>
                  Workspace Scope
                </span>
                <span
                  style={{
                    color: '#0f172a',
                    fontSize: '12px',
                    fontWeight: 600,
                    wordBreak: 'break-word',
                  }}
                >
                  {workspaceName}
                </span>
              </div>
              <div style={{ minWidth: 0 }}>
                <span style={{ color: '#64748b', fontSize: '11px', display: 'block' }}>
                  Campaign
                </span>
                <span
                  style={{
                    color: '#0f172a',
                    fontSize: '12px',
                    fontWeight: 600,
                    wordBreak: 'break-word',
                  }}
                >
                  {campaignName}
                </span>
              </div>
            </div>
          </div>

          {/* Detected Modifications List (When Unmatched) */}
          {!isMatch && detectedDiffs.length > 0 && (
            <div
              style={{
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '12px 14px',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <div
                style={{
                  fontSize: '11.5px',
                  fontWeight: 600,
                  color: '#334155',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <i className="fas fa-edit" style={{ color: '#d97706' }} /> Detected Changes Since
                  Certification
                </span>
                <span
                  style={{
                    background: '#fef3c7',
                    color: '#92400e',
                    border: '1px solid #fde68a',
                    fontSize: '10px',
                    fontWeight: 700,
                    padding: '1px 6px',
                    borderRadius: '4px',
                  }}
                >
                  {detectedDiffs.length} {detectedDiffs.length === 1 ? 'Delta' : 'Deltas'}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {detectedDiffs.map((d, i) => (
                  <div
                    key={i}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      padding: '8px 10px',
                      fontSize: '11.5px',
                      color: '#1e293b',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '3px',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span
                        style={{
                          fontSize: '10px',
                          fontWeight: 600,
                          color: '#475569',
                          background: '#f1f5f9',
                          padding: '1px 5px',
                          borderRadius: '3px',
                        }}
                      >
                        {d.tab}
                      </span>
                      <strong style={{ fontSize: '11.5px', color: '#0f172a' }}>{d.field}</strong>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '11px',
                        color: '#64748b',
                        paddingLeft: '2px',
                        flexWrap: 'wrap',
                      }}
                    >
                      <span style={{ textDecoration: 'line-through', color: '#94a3b8' }}>
                        {d.before}
                      </span>
                      <i
                        className="fas fa-arrow-right"
                        style={{ fontSize: '9px', color: '#cbd5e1' }}
                      />
                      <span style={{ color: '#0f172a', fontWeight: 500 }}>{d.after}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div
                style={{
                  fontSize: '11px',
                  color: '#64748b',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  marginTop: '2px',
                }}
              >
                <i className="fas fa-info-circle" style={{ color: '#3b82f6' }} />
                <span>
                  To certify these new changes, complete digital signature approval in Step 6.
                </span>
              </div>
            </div>
          )}

          {/* Governance Gate Audit Breakdown */}
          <div>
            <div
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#64748b',
                marginBottom: '6px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>Compliance Gates Baseline Verification</span>
              <span style={{ fontSize: '10.5px', color: '#16a34a', fontWeight: 600 }}>
                4 of 4 Gates Passed
              </span>
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '6px',
              }}
            >
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '7px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  fontSize: '11.5px',
                  color: '#334155',
                }}
              >
                <i className="fas fa-check-circle" style={{ color: '#16a34a', fontSize: '12px' }} />
                <span style={{ fontWeight: 500 }}>Brand Voice Alignment</span>
              </div>
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '7px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  fontSize: '11.5px',
                  color: '#334155',
                }}
              >
                <i className="fas fa-check-circle" style={{ color: '#16a34a', fontSize: '12px' }} />
                <span style={{ fontWeight: 500 }}>Commercial Feasibility</span>
              </div>
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '7px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  fontSize: '11.5px',
                  color: '#334155',
                }}
              >
                <i className="fas fa-check-circle" style={{ color: '#16a34a', fontSize: '12px' }} />
                <span style={{ fontWeight: 500 }}>Technical Deliverability</span>
              </div>
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '6px',
                  padding: '7px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '7px',
                  fontSize: '11.5px',
                  color: '#334155',
                }}
              >
                <i className="fas fa-check-circle" style={{ color: '#16a34a', fontSize: '12px' }} />
                <span style={{ fontWeight: 500 }}>Malaysia PDPA Compliance</span>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 20px',
            borderTop: '1px solid #e2e8f0',
            background: '#f8fafc',
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={handleReverify}
            disabled={isVerifying}
            style={{
              background: '#ffffff',
              border: '1px solid #cbd5e1',
              color: '#334155',
              fontWeight: 600,
              padding: '7px 14px',
              fontSize: '12px',
              borderRadius: '6px',
              cursor: isVerifying ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
            }}
          >
            <i
              className={`fas fa-sync-alt ${isVerifying ? 'fa-spin' : ''}`}
              style={{ fontSize: '11px' }}
            />{' '}
            Re-Verify
          </button>

          <button
            type="button"
            onClick={onClose}
            style={{
              fontWeight: 600,
              padding: '7px 20px',
              fontSize: '12px',
              borderRadius: '6px',
              cursor: 'pointer',
              background: '#2563eb',
              color: '#ffffff',
              border: '1px solid #1d4ed8',
              boxShadow: '0 1px 2px rgba(37, 99, 235, 0.2)',
              transition: 'background-color 0.15s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#1d4ed8';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#2563eb';
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
